"use client";

import React, { FC, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import styles from '@/styles/Home.module.css'
import Sidebar, { ItemProps, SubItemProps } from '@apg.gg/core/lib/Sidebar';
import Navbar from '@apg.gg/core/lib/Navbar';
import classNames from 'classnames';
import Button from '@apg.gg/core/lib/Button';
import Dropdown from '@apg.gg/core/lib/Dropdown';
import DropdownItem from '@apg.gg/core/lib/DropdownItem';
import { ApgUser } from '@/domain/account.interface';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import useSidebarStore from '@/store/sidebar.store';
import { useRouter } from 'next-nprogress-bar';
import useAuthStore from '@/store/auth.store';
import { useNotification } from '@/lib/notification.context';
import NotificationCenter from '@/components/SidebarDrawers/NotificationsDrawer';
import { useMessage } from '@/lib/messages.context';
import MailIcon from '@apg.gg/icons/lib/MailIcon';
import { navbarItems } from '../customMenus';
import { cn } from '@apg.gg/core/lib/cn';

interface LayoutAppProps {
  children: React.ReactNode;
  locale: string;
  subItems?: SubItemProps[];
  userProfile?: ApgUser | null;
  authToken?: string;
  userData?: ApgUser | null;
  isLogged: boolean;
}

const LayoutApp: FC<LayoutAppProps> = ({ 
  children,
  subItems, 
  locale,
  userData,
  authToken
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("Common");
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { items, setLocale } = useSidebarStore();
  const { authToken: token, userData: userLogged } = useAuthStore();
  
  const [user, setUser] = useState<ApgUser | null>(userData || userLogged || null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const { unseenCountInitializer, isInitialized } = useNotification();
  const { unseenCountListener, isInitialized: isMessageInitialized, unseenCount } = useMessage();

  // Dropdowns states
  const [profileMenuOpen, setProfileMenuOpen] = useState<boolean>(false);
  
  const [menuItems, setMenuItems] = useState<ItemProps[]>(items);

  const [redirect] = useState<string>(pathname.includes('/auth/') ? '/honor-of-kings' : pathname);

  const mainClasses = pathname.includes('/auth') ? "flex flex-col items-center justify-center gradient-4" : "";

  const classNamesMain = cn(
    "px-4 w-full h-[calc(100vh-56px)] overflow-y-auto scroll-smooth", 
    mainClasses,
    pathname === `/${locale}` ? 'px-0' : ''
  );

  const profileItemMenu = {
    key: 'account-profile',
    name: t('sidebar.account'),
    icon: (
      <div className="rounded-full relative w-10 h-10 group">
        <Image 
          src={userData?.profileImage || ''} 
          className="rounded-full" 
          alt={userData?.name || 'APG Profile'}
          width={40}
          height={40}
        />
      </div>
    ),
    iconHoverColor: "white-300",
    bgColor: "blue-600",
    activeBgColor: "blue-600",
  }

  useEffect(() => {
    setMenuItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    setLocale(locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    setUser(userData || userLogged || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, userLogged])

  useEffect(() => {
    unseenCountInitializer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);
  
  useEffect(() => {
    unseenCountListener();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMessageInitialized]);
  
  return (
    <>
      <Sidebar 
        logo={<Image src="/assets/resources/logo.png" alt='APG.gg' width={40} height={40} />}
        linkLogo={`${process.env.NEXT_PUBLIC_MAIN_URL}/${locale}`}
        menuItems={menuItems}
        recentPages={[]}
        activeItem={pathname.split(`/${locale}`)[1] || ''}
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        searchText={t('navbar.search')}
        tabletCollapsedWidth="64px"
        onToggleDrawer={(value) => {}}
        actions={
          <></>
        }
        actionsMobile={
          <>
            {user ? (
              <div className="mb-4"></div>
            ) : (
              <div className="flex flex-col gap-2 justify-center p-4">
                <Button className="mobile-login-btn" onClick={() => router.push(`/${locale}/auth/login?redirect=${redirect}`)} type="primary">
                  {t("navbar.login-btn")}
                </Button>
                <Button className="mobile-register-btn" onClick={() => router.push(`/${locale}/auth/sign-up?redirect=${redirect}`)} type="outline">
                  {t("navbar.register-btn")}
                </Button>
              </div>
            )}
          </>
        }
      >
        <main className={classNames(
          styles.main,
          "scrollbar-trigger"
        )}>
          <Navbar 
            logo="/assets/resources/logo.png" 
            onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
            actions={
              <>
                {user ? (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => router.push(`${process.env.NEXT_PUBLIC_MAIN_URL}/${locale}/messages`)}
                      type="outline"
                      className="w-8 h-8 !px-0 relative"
                      icon={<MailIcon />}
                    >
                      {unseenCount > 0 ? (
                        <div className="flex absolute -top-1.5 -right-1 text-xs h-4 w-4 bg-blue-400 items-center justify-center whitespace-nowrap text-white rounded-full">
                          <span className="">{unseenCount}</span>
                        </div>
                      ) : null}
                    </Button>
                    <NotificationCenter />
                    <Dropdown 
                      placement="bottomRight"
                      trigger={["click"]}
                      visible={profileMenuOpen}
                      onVisibleChange={(visible) => setProfileMenuOpen(visible)}
                      overlayClassName='bg-black-900 shadow-md rounded p-2 z-50'
                      content={(
                        <div className="flex flex-col gap-1 w-40 bg-">
                          {navbarItems({
                            t,
                            router,
                            locale,
                            username: user.username
                          }).map((item, index) => (
                            <DropdownItem 
                              key={`${item.label}-${index}`} 
                              item={item} 
                              prefixCls='dropdown' 
                              handleItemClick={() => {
                                setProfileMenuOpen(false)
                                item.onClick?.()
                              }} 
                            />
                          ))}
                        </div>
                      )}
                    >
                      <div className="flex gap-2 items-center" role="button" onClick={() => setProfileMenuOpen(!profileMenuOpen)}>
                        <Image className="rounded-full w-8 h-8 object-cover" src={user.profileImage || `${process.env.NEXT_PUBLIC_DEFAULT_PROFILE_PIC}`} alt={user.username} width={100} height={100} />
                        <div className="flex flex-col justify-center">
                          <h4 className="text-label-lg text-white !leading-4">
                            {user.username}
                          </h4>
                          <span className="text-label-md text-black-500">
                            apg.gg/{user.username}
                          </span>
                        </div>
                      </div>
                    </Dropdown>
                  </div>
                ) : (
                  <></>
                )}
              </>
            }
          />
          <div className={classNamesMain} ref={sidebarRef}>
            {children}
          </div>
        </main>
      </Sidebar>
    </>
  )
}

export default LayoutApp;